import { c as _c } from "react/compiler-runtime";
import { useFilterValues } from '../store/selectors/useFilterValues';
import { useEagGridStore } from '../store/useEagGridStore';
import type { ExternalFilters } from '../types/ExternalFilters';
import type { FormToDataGridConnectorProps } from '../types/FormToDataGridConnectorProps';
import { unwrapFilterValues } from '../utils/unwrapFilterValues';
import { wrapFilterValues } from '../utils/wrapFilterValues';

/**
 * Bridges the EagGrid store filter state with external filter components.
 *
 * Returns `{values, onChange}` matching the `FormToDataGridConnectorProps` contract:
 * - `values`: flat `ExternalFilters` derived from the store's wrapped `filterValues`
 * - `onChange`: commits flat values back to the store in wrapped format
 *
 * Used by both `CustomFiltersPanel` (sidebar) and `ExternalFiltersPortal` (portal)
 * to avoid duplicating store↔filter bridge logic.
 *
 */
export function useExternalFilterBridge() {
  const $ = _c(7);
  const store = useEagGridStore();
  const filterValues = useFilterValues();
  let t0;
  if ($[0] !== filterValues) {
    t0 = unwrapFilterValues(filterValues);
    $[0] = filterValues;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const values = t0 as ExternalFilters;
  let t1;
  if ($[2] !== store) {
    t1 = newValues => {
      store.setFilterValues(wrapFilterValues(newValues));
    };
    $[2] = store;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onChange = t1;
  let t2;
  if ($[4] !== onChange || $[5] !== values) {
    t2 = {
      values,
      onChange
    };
    $[4] = onChange;
    $[5] = values;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}